// src/components/dentally/DentallyBookingForm.jsx
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Drawer, Box, Typography, IconButton, Button, Alert, Chip,
  Stack, Switch, FormControlLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { dispatch, useSelector } from '../../redux/store';
import { getDentallyConfigs } from '../../redux/slices/practiceIntegration';
import Iconify from '../Iconify';
import DentallyBookingFormPatient from './DentallyBookingFormPatient';
import DentallyBookingFormAppointment from './DentallyBookingFormAppointment';
import DentallyBookingFormPayment from './DentallyBookingFormPayment';
import DentallyBookingSuccess from './DentallyBookingSuccess';
import { useDentallyBooking } from '../../hooks/useDentallyBooking';
import { drawerPaperStyle, drawerHeaderStyle, drawerBodyStyle, drawerFooterStyle } from '../../styles/DentallyBookingStyle';

const INITIAL_VALUES = {
  // Patient (new lead)
  firstName: '', lastName: '', phone: '', email: '',
  salutation: '', dob: '', postcode: '', sex: '', addressLine1: '',
  // Appointment
  treatmentId: null, treatmentName: null, practitionerId: null, reason: '',
  dateTime: null, durationMinutes: 30,
  method: 'IN_PRACTICE', assignees: [], notes: '',
  // Payment
  paymentOption: 'none', paymentAmount: '', paymentType: 'DEPOSIT', receivedMode: 'PHONE', paymentLinkChannel: 'email',
};

function validate(values, isNewLead, dentallyOnly, existingLead, existingTreatmentId) {
  const errs = {};
  if (isNewLead) {
    if (!values.firstName) errs.firstName = 'Required';
    if (!values.lastName) errs.lastName = 'Required';
    if (!values.phone) errs.phone = 'Required';
    if (!values.email) errs.email = 'Required';
    if (!values.salutation) errs.salutation = 'Required';
    if (!values.dob) errs.dob = 'Required';
    if (!values.postcode) errs.postcode = 'Required';
    if (!values.sex) errs.sex = 'Required';
    if (!values.addressLine1) errs.addressLine1 = 'Required';
  }
  // Existing lead missing DOB — must collect before exporting to Dentally
  if (!isNewLead && existingLead && !existingLead.dob && !values.dob) {
    errs.dob = 'Required';
  }
  // treatmentId is only needed for the CRM path if no existing treatment
  if (!dentallyOnly && !existingTreatmentId && !values.treatmentId) errs.treatmentId = 'Required';
  if (!values.practitionerId) errs.practitionerId = 'Required';
  if (!values.reason) errs.reason = 'Required';
  if (!values.dateTime) errs.dateTime = 'Required';
  if (!values.durationMinutes) errs.durationMinutes = 'Required';
  if (!dentallyOnly && (values.paymentOption === 'link' || values.paymentOption === 'received') && !values.paymentAmount) errs.paymentAmount = 'Required';
  // WhatsApp payment link requires phone even for existing leads
  if (values.paymentOption === 'link') {
    const ch = values.paymentLinkChannel || 'email';
    if ((ch === 'whatsapp' || ch === 'both') && !values.phone && !(existingLead?.lead_contact?.phone)) errs.phone = 'Phone required for WhatsApp';
  }
  return errs;
}

export default function DentallyBookingForm({ open, onClose, existingLead, dentallyOnly, existingTreatmentId }) {
  const navigate = useNavigate();
  const practiceId = useSelector(s => s.practiceTreatmentList.practiceId);
  const dentallyConfigs = useSelector(s => s.practiceIntegration?.dentallyConfigs?.results || []);
  const configExportTrigger = dentallyConfigs.find(c => c.is_active)?.dentally_export_trigger || 'APPOINTMENT';

  // Load Dentally config if not already in Redux
  useEffect(() => {
    if (practiceId && dentallyConfigs.length === 0) {
      dispatch(getDentallyConfigs(practiceId));
    }
  }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [exportTriggerOverride, setExportTriggerOverride] = useState(null);
  const activeExportTrigger = exportTriggerOverride || configExportTrigger;
  const isPaymentRequired = activeExportTrigger === 'PAYMENT';
  const [values, setValues] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});

  const { loading, result, error: bookingError, submit, reset } = useDentallyBooking({
    practiceId,
    existingLead,
    dentallyOnly,
    existingTreatmentId,
    isPaymentRequired,
  });

  useEffect(() => {
    if (!open) return;
    setValues(INITIAL_VALUES);
    setErrors({});
    setExportTriggerOverride(null);
    reset();
  }, [open]);

  const handleChange = useCallback((field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }, []);

  const handleSubmit = () => {
    const errs = validate(values, !existingLead, dentallyOnly, existingLead, existingTreatmentId);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    submit(values);
  };

  const handleClose = () => {
    setValues(INITIAL_VALUES);
    setErrors({});
    reset();
    onClose();
  };

  const handleBookAnother = () => {
    setValues(INITIAL_VALUES);
    setErrors({});
    reset();
  };

  const handleViewLead = (leadId) => {
    handleClose();
    navigate(`/dashboard/practice/${practiceId}/lead-detail/${leadId}`);
  };

  const formContent = result?.success ? (
    <DentallyBookingSuccess
      result={result}
      practiceId={practiceId}
      onViewLead={existingLead ? null : handleViewLead}
      onBookAnother={handleBookAnother}
    />
  ) : (
    <Box>
      {bookingError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {bookingError.message}
        </Alert>
      )}
      <DentallyBookingFormPatient
        existingLead={existingLead}
        values={values}
        onChange={handleChange}
        errors={errors}
      />
      <DentallyBookingFormAppointment
        values={values}
        onChange={handleChange}
        errors={errors}
        openAvailabilityOnMount
      />
      {!dentallyOnly && (
        <>
          {/* Payment mode toggle */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            mb: 2, px: 1.5, py: 1, borderRadius: 1.5,
            bgcolor: isPaymentRequired ? 'rgba(255,171,0,0.08)' : 'rgba(0,171,85,0.06)',
            border: '1px solid',
            borderColor: isPaymentRequired ? 'rgba(255,171,0,0.24)' : 'rgba(0,171,85,0.16)',
          }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify
                icon={isPaymentRequired ? 'mdi:cash-lock' : 'mdi:calendar-check'}
                width={18}
                sx={{ color: isPaymentRequired ? '#FFAB00' : '#00AB55' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
                {isPaymentRequired ? 'Payment required before booking' : 'Book first — payment optional'}
              </Typography>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isPaymentRequired}
                  onChange={(e) => setExportTriggerOverride(e.target.checked ? 'PAYMENT' : 'APPOINTMENT')}
                />
              }
              label={<Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Require payment</Typography>}
              sx={{ m: 0 }}
            />
          </Box>
          <DentallyBookingFormPayment
            values={values}
            onChange={handleChange}
            errors={errors}
          />
          {isPaymentRequired && values.paymentOption === 'none' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Payment required before appointment is confirmed.
            </Alert>
          )}
        </>
      )}
    </Box>
  );

  // Confirm button label + icon based on payment option
  const CONFIRM_MAP = {
    received: { label: 'Confirm & Record Payment', icon: 'mdi:cash-check' },
    link: { label: 'Confirm & Send Link', icon: 'mdi:send-check' },
  };
  const { label: confirmLabel, icon: confirmIcon } = CONFIRM_MAP[values.paymentOption] || { label: 'Confirm Booking', icon: 'mdi:calendar-check' };

  // Existing lead → right-side Drawer
  if (existingLead) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: drawerPaperStyle }}
      >
        <Box sx={drawerHeaderStyle}>
          <Typography variant="h6" fontWeight={700}>Book Dentally Appointment</Typography>
          <IconButton size="small" onClick={handleClose}>
            <Iconify icon="mdi:close" width={20} />
          </IconButton>
        </Box>
        <Box sx={drawerBodyStyle}>{formContent}</Box>
        {!result?.success && (
          <Box sx={drawerFooterStyle}>
            <LoadingButton
              fullWidth
              variant="contained"
              loading={loading}
              disabled={!dentallyOnly && isPaymentRequired && values.paymentOption === 'none'}
              startIcon={<Iconify icon={confirmIcon} />}
              onClick={handleSubmit}
            >
              {confirmLabel}
            </LoadingButton>
          </Box>
        )}
      </Drawer>
    );
  }

  // New lead → centred Dialog
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="dentally-booking-title">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Iconify icon="mdi:calendar-plus" width={24} sx={{ color: '#00AB55' }} />
          <Typography id="dentally-booking-title" variant="h6" fontWeight={700}>Book Dentally Appointment</Typography>
          {dentallyOnly && (
            <Chip
              label="Dentally Only · No CRM lead"
              size="small"
              sx={{ bgcolor: 'rgba(24,144,255,0.1)', color: 'primary.main', fontWeight: 600, fontSize: 11 }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <Iconify icon="mdi:close" width={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>{formContent}</DialogContent>
      {!result?.success && (
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>Cancel</Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={!dentallyOnly && isPaymentRequired && values.paymentOption === 'none'}
            startIcon={<Iconify icon={confirmIcon} />}
            onClick={handleSubmit}
          >
            {confirmLabel}
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

DentallyBookingForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  existingLead: PropTypes.object,
  dentallyOnly: PropTypes.bool,
  existingTreatmentId: PropTypes.number,
};

DentallyBookingForm.defaultProps = {
  existingLead: null,
  dentallyOnly: false,
  existingTreatmentId: null,
};
